import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';

import { Container, Link, SectionText } from '@/atoms';
import { SwiperCustom } from '@/molecules';
import { ApartmentCard } from '@/organisms';

import type { FunctionComponent } from 'react';
import type { TypeApartmentsProps } from './types';

const Tabs = dynamic(() => import('@/atoms/tabs'));

/**
 * Apartments
 */
export const Apartments: FunctionComponent<TypeApartmentsProps> = ({
  className = '',
  apartments,
  backgroundColorBottom,
  backgroundColorTop,
  caption,
  cutLevel = 50,
  desc,
  title,
  link,
  cutLevelMobile = 50,
  textColor,
  index,
  id,
  monthday = 'Month',
  hasCutLevel,
}: TypeApartmentsProps) => {
  const { t } = useTranslation('common');
  const tabsName = [t('prices.priceMonth'), t('prices.priceDay')];

  const apartmentsMonth = apartments?.apartmentsCards?.filter(
    (itm) => itm.tipoestancia === 'month'
  );

  const apartmentsDay = apartments?.apartmentsCards?.filter(
    (itm) => itm.tipoestancia !== 'month'
  );

  const longitud = apartmentsDay.length + apartmentsMonth.length;
  const isApartments = process.env.NEXT_PUBLIC_DOMAIN === 'apartments';

  //Cutlevel y cutlevelmobile están intercambiados por alguna razón, no he encontrado la causa, pero con invertirlos aquí es suficiente. SI te animas buena suerte con ello, en el resto de componentes funciona normal
  return (
    <Container
      index={index}
      backgroundColorTop={backgroundColorTop ?? '#fff'}
      backgroundColorBottom={backgroundColorBottom ?? '#fff'}
      cutLevel={cutLevelMobile === null ? 50 : cutLevelMobile}
      cutLevelMobile={cutLevel === null ? 50 : cutLevel}
      backgroundColorBottomMobile={backgroundColorBottom}
      backgroundColorTopMobile={backgroundColorTop}
      hasCutLevel={hasCutLevel}
      overflows
      className={`${
        isApartments
          ? 'flex-col'
          : `flex-col ${longitud > 1 ? 'md:flex-row' : ''}`
      }  flex gap-10 ${className}`}
      id={id}
    >
      <SectionText
        title={title}
        directionMobile="column"
        desc={desc}
        caption={caption}
        direction={longitud > 1 ? 'row' : 'column'}
        textAlign="start"
        className={` ${longitud > 1 ? '' : 'w-full md:max-w-[33%]'}`}
        variant={textColor}
        overflows
      />
      {apartmentsMonth.length > 0 && apartmentsDay.length > 0 ? (
        <Tabs
          tabsName={tabsName}
          type="default"
          selectedAtFirst={0}
          cardRotation
          overflows
        >
          <SwiperCustom
            scrollbar={true}
            navigation
            slidesPerView={longitud > 1 ? 3 : 1}
            slidesPerViewTablet={longitud > 1 ? 2.2 : 1}
            slidesPerViewMobile={longitud > 1 ? 1.2 : 1}
            gap={24}
            overflows
          >
            {apartmentsMonth?.map((apartmentsCards, index) => (
              <ApartmentCard
                {...apartmentsCards}
                key={index}
                monthday="Month"
                className="h-full"
              />
            ))}
          </SwiperCustom>

          <SwiperCustom
            scrollbar={true}
            navigation
            slidesPerView={longitud > 1 ? 3 : 1}
            slidesPerViewTablet={longitud > 1 ? 2.2 : 1}
            slidesPerViewMobile={longitud > 1 ? 1.2 : 1}
            gap={24}
            overflows
          >
            {apartmentsDay?.map((apartmentsCards, index) => (
              <ApartmentCard
                {...apartmentsCards}
                key={index}
                monthday="Day"
                className="h-full"
              />
            ))}
          </SwiperCustom>
        </Tabs>
      ) : (
        <div
          className={`${longitud > 1 ? '' : 'h-full w-full md:max-w-[66%]'}`}
        >
          <SwiperCustom
            scrollbar={true}
            navigation
            slidesPerView={longitud > 1 ? 3 : 1}
            slidesPerViewTablet={longitud > 1 ? 2.2 : 1}
            slidesPerViewMobile={longitud > 1 ? 1.2 : 1}
            gap={24}
            className={`${longitud > 1 ? '' : ' h-full'}`}
          >
            {(apartmentsMonth || apartmentsDay)?.map(
              (apartmentsCards, index) => (
                <ApartmentCard
                  key={index}
                  {...apartmentsCards}
                  monthday={monthday}
                  cardOrientation={
                    isApartments || longitud > 1 ? 'column' : 'row'
                  }
                  buttonWidth={100}
                  className="h-full"
                />
              )
            )}
          </SwiperCustom>
        </div>
      )}

      <>
        {link?.link && link?.text && (
          <div className="flex !w-full max-w-[calc(100%-1.25rem)] justify-center md:max-w-wrapper md:mx-0 md:justify-end md:px-5 lg:px-0 xl:max-w-[calc(var(--wrapper-size)-2.5rem)]">
            <NextLink passHref prefetch={false} href={link.link}>
              <Link arrow={true} as="a" variant={link?.variant}>
                {link.text}
              </Link>
            </NextLink>
          </div>
        )}
      </>
    </Container>
  );
};

Apartments.displayName = 'Apartments';
